import { ButtonGroup } from "../common/Button";
import { faMagnifyingGlass, faPlay, faStop, faPlayCircle } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { styled } from "styled-components";
import { Tabs, TabItem, TabContent, TabItemBadge } from "../common/Tabs";
import { tradeGoodInContract } from "../../utils/helpers";
import { useEffect, useState } from "react";
import Button from "../common/Button";
import Flow from "../common/Flow";
import Modal from "../common/Modal";
import Select from "../common/Select";
import Table, { TableBar } from "../common/Table";
import Typography from "../common/Typography";
import WaypointList from "../systems/WaypointList";

const Cockpit = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: space-between;
`;

const Waypoints = styled.div`
  border-left: var(--border);
  flex-basis: 312px;
  flex-grow: 0;
  flex-shrink: 0;
  height: 100%;
  overflow-y: scroll;
  padding-inline-start: var(--space-6);
  scrollbar-width: 0;
`;

const WaypointSearch = styled.div`
  display: flex;
  position: sticky;
  top: 0;
  left: 0;
  background-color: var(--window-background);
  z-index: 20;

  input:read-only {
    border-right: 0;
    width: calc(var(--space-4) + 8ch);
  }

  svg {
    position: absolute;
    right: var(--space-2);
    top: 50%;
    translate: 0 -50%;
  }
`;

const ShipSpecs = styled.div`
  display: flex;
  gap: var(--space-6);

  > div {
    border: var(--border);
    flex: 1;
    padding: var(--space-6) var(--space-3);
    position: relative;
  }
`;

const ShipSpecsTitle = styled(Typography)`
  background-color: var(--window-background);
  left: 0;
  padding: var(--space-3);
  position: absolute;
  top: 0;
  translate: 0 -50%;
`;

function ShipModal({ store }) {
  const modal = store.state.modal;
  const system = store.state.system;
  const contracts = store.state.contracts;
  const ship = store.getSelectedShip();
  const autopilot = (store.state.autopilot && ship) ? store.state.autopilot[ship.symbol] : null;
  const [tab, setTab] = useState(modal.tab ? modal.tab : 'info');
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState(system.waypoints);

  const [waypoint1, setWaypoint1] = useState(autopilot ? autopilot.waypoint1 : '');
  const [waypoint2, setWaypoint2] = useState(autopilot ? autopilot.waypoint2 : '');
  const [waypoint3, setWaypoint3] = useState(autopilot ? autopilot.waypoint3 : '');
  const [cargo1, setCargo1] = useState(autopilot ? autopilot.cargo1 : '');

  const saveAutopilot = () => {
    if (ship) {
      store.saveAutopilot(ship.symbol, {waypoint1, waypoint2, waypoint3, cargo1});
    }
  };

  const jettison = (item) => {
    if (window.confirm("Really jettison this cargo into space?")) {
      store.jettison(item.symbol, item.units);
    }
  }

  const refine = (item) => {
    store.refine(item.symbol.replace('_ORE', ''));
  }

  useEffect(() => {
    if (system) {
      const result = system.waypoints.filter(waypoint => waypoint.symbol.includes(search.toUpperCase()));
      setSearchResults(result);
    }

  }, [search, system]);

  return (
    <Modal store={store}>
      <Cockpit>
        <Flow $space={6}>
          <Typography as="h2" fontSize="2xl">{ship && ship.symbol} ({ship && ship.frame.name})</Typography>
          <Tabs>
            <TabItem $active={tab == 'info'} onClick={() => setTab('info')}>Information</TabItem>
            <TabItem $active={tab == 'cargo'} onClick={() => setTab('cargo')}>Cargo</TabItem>
            <TabItem $active={tab == 'modules'} onClick={() => setTab('modules')}>Modules</TabItem>
            <TabItem $active={tab == 'mounts'} onClick={() => setTab('mounts')}>Mounts</TabItem>
            <TabItem $active={tab == 'autoPilot'} onClick={() => setTab('autoPilot')}>
              Auto pilot
              {ship && ship.autopilot &&
                <TabItemBadge>
                  <FontAwesomeIcon size="lg" icon={faPlayCircle} />
                </TabItemBadge>
              }
            </TabItem>
          </Tabs>
          {tab == 'info' &&
            <TabContent>
              <Flow $space={10}>
                <Table>
                  <tbody>
                    <tr>
                      <th>Status</th>
                      <td>{ship && ship.nav.status}</td>
                    </tr>
                    <tr>
                      <th>Flight mode</th>
                      <td>{ship && ship.nav.flightMode}</td>
                    </tr>
                    <tr>
                      <th>Location</th>
                      <td>{ship && ship.nav.waypointSymbol}</td>
                    </tr>
                    <tr>
                      <th className="middle">Fuel</th>
                      <td>
                        <div>
                          <div>{ship && ship.fuel.current}/{ship && ship.fuel.capacity}</div>
                          <TableBar title={`${ship && ship.fuel.current}/${ship && ship.fuel.capacity}`} $current={ship && ship.fuel.current} $max={ship && ship.fuel.capacity} />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th className="middle">Cargo</th>
                      <td>
                        <div>{ship && ship.cargo.units}/{ship && ship.cargo.capacity}</div>
                        <TableBar title={`${ship && ship.cargo.units}/${ship && ship.cargo.capacity}`} $current={ship && ship.cargo.units} $max={ship && ship.cargo.capacity} />
                      </td>
                    </tr>
                  </tbody>
                </Table>
                <ShipSpecs>
                  <div>
                    <div className="title">
                      <ShipSpecsTitle as="h2" fontSize="lg">Engine</ShipSpecsTitle>
                    </div>
                    <Table>
                      <tbody>
                        <tr>
                          <th>name</th>
                          <td>{ship && ship.engine.name}</td>
                        </tr>
                        <tr>
                          <th>Speed</th>
                          <td>{ship && ship.engine.speed}</td>
                        </tr>
                        <tr>
                          <th>Description</th>
                          <td>{ship && ship.engine.description}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                  <div>
                    <div className="title">
                      <ShipSpecsTitle as="h2" fontSize="lg">Reactor</ShipSpecsTitle>
                    </div>
                    <Table>
                      <tbody>
                        <tr>
                          <th>name</th>
                          <td>{ship && ship.reactor.name}</td>
                        </tr>
                        <tr>
                          <th>Power</th>
                          <td>{ship && ship.reactor.powerOutput}</td>
                        </tr>
                        <tr>
                          <th>Description</th>
                          <td>{ship && ship.reactor.description}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                  <div>
                    <div className="title">
                      <ShipSpecsTitle as="h2" fontSize="lg">Frame</ShipSpecsTitle>
                    </div>
                    <Table>
                      <tbody>
                        <tr>
                          <th>name</th>
                          <td>{ship && ship.frame.name}</td>
                        </tr>
                        <tr>
                          <th>Module slots</th>
                          <td>{ship && ship.modules.length}/{ship && ship.frame.moduleSlots}</td>
                        </tr>
                        <tr>
                          <th>Mounting points</th>
                          <td>{ship && ship.mounts.length}/{ship && ship.frame.mountingPoints}</td>
                        </tr>
                        <tr>
                          <th>Description</th>
                          <td>{ship && ship.frame.description}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </ShipSpecs>
              </Flow>
            </TabContent>
          }
          {tab == 'cargo' &&
            <TabContent>
              <Table $style="striped">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Name</th>
                    <th>Units</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {ship && ship.cargo.inventory.map((item, idx) => (
                    <tr key={idx} className={tradeGoodInContract(item.symbol, contracts) ? 'highlight' : undefined}>
                      <td>{item.symbol}</td>
                      <td>{item.name}</td>
                      <td>{item.units}</td>
                      <td>
                        <ButtonGroup>
                          <Button $action onClick={() => jettison(item)}>Jettison</Button>
                          <Button $action onClick={() => refine(item)}>Refine</Button>
                          {item.symbol.includes('MOUNT') && (
                            <Button $action onClick={() => store.install(item.symbol)}>Install</Button>
                          )}
                        </ButtonGroup>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TabContent>
          }
          {tab == 'modules' &&
            <TabContent>
              <Table $style="striped">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Name</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {ship && ship.modules.map((module, idx) => (
                    <tr key={idx}>
                      <td>{module.symbol}</td>
                      <td>{module.name}</td>
                      <td>{module.description}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TabContent>
          }
          {tab == 'mounts' &&
            <TabContent>
              <Table $style="striped">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Name</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {ship && ship.mounts.map((mount, idx) => (
                    <tr key={idx}>
                      <td>{mount.symbol}</td>
                      <td>{mount.name}</td>
                      <td>{mount.description}</td>
                      <td>
                        <ButtonGroup>
                          <Button $action onClick={() => store.uninstall(mount.symbol)}>Uninstall</Button>
                        </ButtonGroup>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TabContent>
          }
          {tab == 'autoPilot' &&
            <TabContent>
              <Flow $space={3}>
                <div>
                  <label>Mine at</label>
                  <Select selected={waypoint1} onBlur={saveAutopilot} onChange={(e) => setWaypoint1(e.target.value)}>
                    <option value=""></option>
                    {system && system.waypoints.map(waypoint => <option key={waypoint.symbol} value={waypoint.symbol}>{waypoint.symbol}</option>)}
                  </Select>
                </div>

                <div>
                  <label>Sell everything at</label>
                  <Select selected={waypoint2} onBlur={saveAutopilot} onChange={(e) => setWaypoint2(e.target.value)}>
                    <option value=""></option>
                    {system && system.waypoints.map(waypoint => <option key={waypoint.symbol} value={waypoint.symbol}>{waypoint.symbol}</option>)}
                  </Select>
                </div>

                <div>
                  <label>Do not sell this cargo (optional)</label>
                  <Select selected={cargo1} onBlur={saveAutopilot} onChange={(e) => setCargo1(e.target.value)}>
                    <option value=""></option>
                    <option value="ALUMINUM_ORE">Aluminum</option>
                    <option value="IRON_ORE">Iron</option>
                    <option value="COPPER_ORE">Copper</option>
                  </Select>
                </div>

                <div>
                  <label>Deliver at (optional)</label>
                  <Select selected={waypoint3} onBlur={saveAutopilot} onChange={(e) => setWaypoint3(e.target.value)}>
                    <option value=""></option>
                    {system && system.waypoints.map(waypoint => <option key={waypoint.symbol} value={waypoint.symbol}>{waypoint.symbol}</option>)}
                  </Select>
                </div>

                { autopilot && waypoint1 && waypoint2 &&
                  <Button $action="icon" $disabled={!autopilot} title={`Start autopilot`} onClick={() => store.toggleAutopilot(ship.symbol)} type="submit">
                    <FontAwesomeIcon size="lg" icon={ship.autopilot ? faStop : faPlay} />
                  </Button>
                }
              </Flow>
            </TabContent>
          }
        </Flow>
      </Cockpit>
      <Waypoints>
        <Flow $space={6}>
          <Typography as="h2" fontSize="xl">System {store.state.system.symbol}</Typography>
          <Flow $space={3}>
            <WaypointSearch>
              <input value={system.symbol + '-'} readOnly />
              <input type="text" onChange={(e) => setSearch(e.target.value)} placeholder="Search for waypoint" />
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </WaypointSearch>
            <WaypointList waypoints={searchResults && searchResults} />
          </Flow>
        </Flow>
      </Waypoints>
    </Modal>
  );
}

export default ShipModal;
